import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Icon } from "@iconify/react/dist/iconify.js";
import { getUsers, deleteUser } from "../../services/userService";
import { UserModal } from "./UserModal";
import { UserWalletHistoryModal } from "./UserWalletHistoryModal";
import { UserPasswordModal } from "./UserPasswordModal";
import { UserPaymentDetailsModal } from "./UserPaymentDetailsModal";

/* ─── helpers ─── */
const toastCfg = { autoClose: 1500 };

const roleMeta = {
  super_admin: { label: "Super Admin", bg: "#1e293b", color: "#fff" },
  agent:       { label: "Agent",       bg: "#f59e0b", color: "#1a1a1a" },
  demo:        { label: "Demo",        bg: "#64748b", color: "#fff" },
  user:        { label: "User",        bg: "#3b82f6", color: "#fff" },
};

const avatarColors = [
  "#4B5EAA","#16632b","#e05252","#F0A500","#7B6CF6","#3B9FE8","#e67e22","#2ecc71",
];
const avatarBg = (name = "") =>
  avatarColors[(name.charCodeAt(0) || 0) % avatarColors.length];
const initials = (name = "") =>
  name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

const formatDate = (val) => {
  if (!val) return "—";
  const d = new Date(typeof val === "string" ? val.replace(" ", "T") : val);
  if (isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};
const formatTime = (val) => {
  if (!val) return "";
  const d = new Date(typeof val === "string" ? val.replace(" ", "T") : val);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
};

/* ─── Sort icon ─── */
const SortIcon = ({ col }) => {
  if (!col.getCanSort()) return null;
  const dir = col.getIsSorted();
  return (
    <span style={{ marginLeft: 4, opacity: dir ? 1 : 0.35, fontSize: 11 }}>
      {dir === "asc" ? "▲" : dir === "desc" ? "▼" : "⇅"}
    </span>
  );
};

/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
export const UserLayerList = () => {
  const [users,    setUsers]    = useState([]);
  const [loading,  setLoading]  = useState(true);

  /* modal state */
  const [showModal, setShowModal] = useState(false);
  const [editUser,  setEditUser]  = useState(null);

  /* new modals */
  const [walletUser,  setWalletUser]  = useState(null);
  const [passwordUser,setPasswordUser]= useState(null);
  const [paymentUser, setPaymentUser] = useState(null);

  /* table state */
  const [globalFilter, setGlobalFilter] = useState("");
  const [roleFilter,   setRoleFilter]   = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [fromDate,     setFromDate]     = useState("");
  const [toDate,       setToDate]       = useState("");
  const [sorting,      setSorting]      = useState([]);
  const [pagination,   setPagination]   = useState({ pageIndex: 0, pageSize: 10 });

  /* ── fetch ── */
  const fetchUsers = useCallback(async () => {
    try {
      const res = await getUsers();
      if (res?.status) setUsers(res.data || []);
    } catch {
      toast.error("Failed to load users", toastCfg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    const t = setInterval(() => fetchUsers(), 30000);
    return () => clearInterval(t);
  }, [fetchUsers]);

  /* ── delete ── */
  const handleDelete = useCallback(async (u) => {
    if (!window.confirm(`Delete user "${u.name}"?`)) return;
    try {
      await deleteUser(u.id);
      toast.success("User deleted", toastCfg);
      fetchUsers();
    } catch {
      toast.error("Delete failed", toastCfg);
    }
  }, [fetchUsers]);

  /* ── open modal ── */
  const openAdd  = () => { setEditUser(null);  setShowModal(true); };
  const openEdit = (u) => { setEditUser(u);    setShowModal(true); };

  /* ── filtered data ── */
  const data = useMemo(() => {
    const fromTs = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : null;
    const toTs   = toDate   ? new Date(`${toDate}T23:59:59`).getTime()   : null;
    return users.filter((u) => {
      if (roleFilter   !== "all" && u.role     !== roleFilter)          return false;
      if (statusFilter !== "all" && String(u.status) !== statusFilter)  return false;
      if (fromTs || toTs) {
        if (!u.created_at) return false;
        const ts = new Date(String(u.created_at).replace(" ", "T")).getTime();
        if (isNaN(ts)) return false;
        if (fromTs && ts < fromTs) return false;
        if (toTs   && ts > toTs)   return false;
      }
      return true;
    });
  }, [users, roleFilter, statusFilter, fromDate, toDate]);

  /* ── stats ── */
  const stats = useMemo(() => ({
    total:    users.length,
    active:   users.filter((u) => String(u.status) === "1").length,
    inactive: users.filter((u) => String(u.status) === "0").length,
    agents:   users.filter((u) => u.role === "agent").length,
  }), [users]);

  /* ── columns ── */
  const columns = useMemo(() => [
    {
      id: "serial",
      header: "#",
      size: 44,
      enableSorting: false,
      cell: ({ row }) => row.index + 1,
    },
    {
      accessorKey: "uid",
      header: "UID",
      size: 80,
      cell: ({ getValue }) => (
        <span style={ts.uid}>{getValue() || "—"}</span>
      ),
    },
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => {
        const u = row.original;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <div style={{
              width: 30, height: 30, borderRadius: "50%",
              background: avatarBg(u.name), color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "0.7rem", fontWeight: 700, flexShrink: 0,
            }}>
              {initials(u.name)}
            </div>
            <div>
              <div style={ts.name}>{u.name}</div>
              <div style={ts.email}>{u.email}</div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "mobile",
      header: "Mobile",
      cell: ({ getValue }) => <span style={ts.mono}>{getValue()}</span>,
    },
    {
      accessorKey: "wallet_balance",
      header: "Wallet",
      cell: ({ getValue }) => (
        <span style={ts.wallet}>₹{Number(getValue() || 0).toLocaleString("en-IN")}</span>
      ),
    },
    {
      accessorKey: "role",
      header: "Role",
      cell: ({ getValue }) => {
        const m = roleMeta[getValue()] || roleMeta.user;
        return (
          <span style={{ ...ts.badge, background: m.bg, color: m.color }}>
            {m.label}
          </span>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const active = String(getValue()) === "1";
        return (
          <span style={{
            ...ts.badge,
            background: active ? "#dcfce7" : "#fee2e2",
            color:      active ? "#16a34a" : "#dc2626",
          }}>
            {active ? "● Active" : "○ Inactive"}
          </span>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: "Created",
      size: 140,
      cell: ({ getValue }) => {
        const v = getValue();
        return (
          <div style={{ lineHeight: 1.25 }}>
            <div style={ts.dateMain}>{formatDate(v)}</div>
            <div style={ts.dateSub}>{formatTime(v)}</div>
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      size: 200,
      enableSorting: false,
      cell: ({ row }) => (
        <div style={ts.actionGroup}>
          <button
            style={{ ...ts.iconBtn, ...ts.iconEdit }}
            onClick={() => openEdit(row.original)}
            title="Edit user"
          >
            <Icon icon="solar:pen-bold" style={{ fontSize: 14 }} />
          </button>
          <button
            style={{ ...ts.iconBtn, ...ts.iconWallet }}
            onClick={() => setWalletUser(row.original)}
            title="Wallet History"
          >
            <Icon icon="solar:wallet-bold" style={{ fontSize: 14 }} />
          </button>
          <button
            style={{ ...ts.iconBtn, ...ts.iconPassword }}
            onClick={() => setPasswordUser(row.original)}
            title="Reset Password"
          >
            <Icon icon="solar:lock-password-bold" style={{ fontSize: 14 }} />
          </button>
          <button
            style={{ ...ts.iconBtn, ...ts.iconPayment }}
            onClick={() => setPaymentUser(row.original)}
            title="Payment Details"
          >
            <Icon icon="solar:card-bold" style={{ fontSize: 14 }} />
          </button>
          <span style={ts.actionDivider} />
          <button
            style={{ ...ts.iconBtn, ...ts.iconDelete }}
            onClick={() => handleDelete(row.original)}
            title="Delete user"
          >
            <Icon icon="solar:trash-bin-trash-bold" style={{ fontSize: 14 }} />
          </button>
        </div>
      ),
    },
  ], [handleDelete]);

  /* ── table instance ── */
  const table = useReactTable({
    data,
    columns,
    state: { globalFilter, sorting, pagination },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel:       getCoreRowModel(),
    getFilteredRowModel:   getFilteredRowModel(),
    getSortedRowModel:     getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: (row, _colId, filterValue) => {
      const v = filterValue.toLowerCase();
      return (
        row.original.name?.toLowerCase().includes(v)   ||
        row.original.mobile?.includes(v)               ||
        row.original.email?.toLowerCase().includes(v)  ||
        (row.original.uid || "").toLowerCase().includes(v)
      );
    },
  });

  const { pageIndex, pageSize } = table.getState().pagination;
  const pageCount = table.getPageCount();
  const filteredCount = table.getFilteredRowModel().rows.length;
  const firstRow = filteredCount === 0 ? 0 : pageIndex * pageSize + 1;
  const lastRow  = Math.min((pageIndex + 1) * pageSize, filteredCount);

  return (
    <div style={s.root}>
      {/* ── Stats row ── */}
      <div style={s.statsRow}>
        {[
          { label: "Total Users",  value: stats.total,    icon: "solar:users-group-two-rounded-bold-duotone", color: "#4B5EAA" },
          { label: "Active",       value: stats.active,   icon: "solar:check-circle-bold-duotone",            color: "#16a34a" },
          { label: "Inactive",     value: stats.inactive, icon: "solar:close-circle-bold-duotone",            color: "#dc2626" },
          { label: "Agents",       value: stats.agents,   icon: "solar:user-id-bold-duotone",                 color: "#F0A500" },
        ].map((c) => (
          <div key={c.label} style={s.statCard}>
            <div style={{ ...s.statIcon, background: `${c.color}14`, color: c.color }}>
              <Icon icon={c.icon} style={{ fontSize: 20 }} />
            </div>
            <div>
              <div style={s.statValue}>{c.value}</div>
              <div style={s.statLabel}>{c.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Card ── */}
      <div style={s.card}>
        {/* Toolbar */}
        <div style={s.toolbar}>
          <div style={s.toolbarLeft}>
            <div style={s.searchWrap}>
              <Icon icon="ion:search-outline" style={s.searchIcon} />
              <input
                style={s.searchInput}
                placeholder="Search name, mobile, email, UID…"
                value={globalFilter}
                onChange={(e) => { setGlobalFilter(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}
              />
              {globalFilter && (
                <button style={s.clearBtn} onClick={() => setGlobalFilter("")}>
                  <Icon icon="radix-icons:cross-2" style={{ fontSize: 12 }} />
                </button>
              )}
            </div>

            <select style={s.select} value={roleFilter}
              onChange={(e) => { setRoleFilter(e.target.value); setPagination((p) => ({...p, pageIndex: 0})); }}>
              <option value="all">All Roles</option>
              <option value="user">User</option>
              <option value="agent">Agent</option>
            </select>

            <select style={s.select} value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPagination((p) => ({...p, pageIndex: 0})); }}>
              <option value="all">All Status</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>

            <div style={s.dateGroup}>
              <Icon icon="solar:calendar-bold-duotone" style={{ fontSize: 15, color: "#64748b" }} />
              <span style={s.dateLabel}>By Created Date</span>
              <input
                type="date"
                style={s.dateInput}
                value={fromDate}
                max={toDate || undefined}
                onChange={(e) => { setFromDate(e.target.value); setPagination((p) => ({...p, pageIndex: 0})); }}
                title="From date"
              />
              <span style={s.dateSep}>→</span>
              <input
                type="date"
                style={s.dateInput}
                value={toDate}
                min={fromDate || undefined}
                onChange={(e) => { setToDate(e.target.value); setPagination((p) => ({...p, pageIndex: 0})); }}
                title="To date"
              />
              {(fromDate || toDate) && (
                <button
                  style={s.dateClearBtn}
                  onClick={() => { setFromDate(""); setToDate(""); setPagination((p) => ({...p, pageIndex: 0})); }}
                  title="Clear date range"
                >
                  <Icon icon="radix-icons:cross-2" style={{ fontSize: 12 }} />
                </button>
              )}
            </div>
          </div>

          <div style={s.toolbarRight}>
            <select style={s.select} value={pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}>
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>Show {n}</option>
              ))}
            </select>

            <button style={s.addBtn} onClick={openAdd}>
              <Icon icon="solar:user-plus-bold" style={{ fontSize: 15 }} />
              Add User
            </button>
          </div>
        </div>

        {/* Table */}
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        ...s.th,
                        width: header.column.getSize() || undefined,
                        cursor: header.column.getCanSort() ? "pointer" : "default",
                        userSelect: "none",
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <SortIcon col={header.column} />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} style={s.emptyCell}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", color: "#94a3b8" }}>
                      <span className="spinner-border spinner-border-sm" />
                      Loading users…
                    </div>
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} style={s.emptyCell}>
                    <Icon icon="solar:users-group-rounded-bold-duotone" style={{ fontSize: 36, color: "#cbd5e1", display: "block", margin: "0 auto 8px" }} />
                    No users match your filters
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, idx) => (
                  <tr key={row.id} style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#f0fdf4"}
                    onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbfc"}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} style={s.td}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        <div style={s.pagination}>
          <div style={s.pgInfo}>
            {filteredCount === 0
              ? "No users found"
              : <>Showing <strong>{firstRow}–{lastRow}</strong> of <strong>{filteredCount}</strong> users</>
            }
          </div>
          <div style={s.pgBtns}>
            <button style={s.pgBtn} onClick={() => table.setPageIndex(0)}              disabled={!table.getCanPreviousPage()}>«</button>
            <button style={s.pgBtn} onClick={() => table.previousPage()}                disabled={!table.getCanPreviousPage()}>‹</button>
            {Array.from({ length: Math.min(pageCount, 7) }, (_, i) => {
              const start = Math.max(0, Math.min(pageIndex - 3, pageCount - 7));
              const pg = start + i;
              return (
                <button key={pg}
                  style={{ ...s.pgBtn, ...(pg === pageIndex ? s.pgBtnActive : {}) }}
                  onClick={() => table.setPageIndex(pg)}>
                  {pg + 1}
                </button>
              );
            })}
            <button style={s.pgBtn} onClick={() => table.nextPage()}                    disabled={!table.getCanNextPage()}>›</button>
            <button style={s.pgBtn} onClick={() => table.setPageIndex(pageCount - 1)}   disabled={!table.getCanNextPage()}>»</button>
          </div>
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      <UserModal
        show={showModal}
        onHide={() => setShowModal(false)}
        editUser={editUser}
        onSaved={fetchUsers}
      />

      {/* ── Wallet History Modal ── */}
      <UserWalletHistoryModal
        show={!!walletUser}
        onHide={() => setWalletUser(null)}
        user={walletUser}
      />

      {/* ── Password Modal ── */}
      <UserPasswordModal
        show={!!passwordUser}
        onHide={() => setPasswordUser(null)}
        user={passwordUser}
      />

      {/* ── Payment Details Modal ── */}
      <UserPaymentDetailsModal
        show={!!paymentUser}
        onHide={() => setPaymentUser(null)}
        user={paymentUser}
      />
    </div>
  );
};

/* ─── Page styles ─── */
const s = {
  root: { padding: "20px 24px 40px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },

  /* stats */
  statsRow: { display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 16 },
  statCard: {
    background: "#fff", borderRadius: 14, padding: "14px 16px",
    display: "flex", alignItems: "center", gap: 12,
    border: "1px solid #e8edf2",
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  },
  statIcon: { width: 40, height: 40, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  statValue: { fontSize: "1.4rem", fontWeight: 800, color: "#0f172a", lineHeight: 1 },
  statLabel: { fontSize: "0.72rem", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 2 },

  /* card */
  card: { background: "#fff", borderRadius: 16, border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden" },

  /* toolbar */
  toolbar: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "12px 16px", borderBottom: "1px solid #f1f5f9",
    flexWrap: "wrap", gap: 10,
  },
  toolbarLeft:  { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  toolbarRight: { display: "flex", alignItems: "center", gap: 8 },

  searchWrap: { position: "relative", display: "flex", alignItems: "center" },
  searchIcon: { position: "absolute", left: 10, fontSize: 15, color: "#94a3b8", pointerEvents: "none" },
  searchInput: {
    padding: "7px 30px 7px 32px",
    border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.82rem", color: "#334155", background: "#f8fafc",
    outline: "none", width: 240, fontFamily: "inherit",
  },
  clearBtn: {
    position: "absolute", right: 8, background: "none", border: "none",
    cursor: "pointer", color: "#94a3b8", padding: 0,
    display: "flex", alignItems: "center",
  },
  select: {
    padding: "7px 10px",
    border: "1.5px solid #e2e8f0", borderRadius: 9,
    fontSize: "0.81rem", color: "#475569", background: "#f8fafc",
    outline: "none", cursor: "pointer", fontFamily: "inherit",
  },
  dateGroup: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "4px 8px",
    border: "1.5px solid #e2e8f0", borderRadius: 9,
    background: "#f8fafc",
  },
  dateInput: {
    border: "none", outline: "none", background: "transparent",
    fontSize: "0.81rem", color: "#475569", fontFamily: "inherit",
    padding: "2px 0", cursor: "pointer",
  },
  dateLabel: { fontSize: "0.76rem", fontWeight: 600, color: "#475569", whiteSpace: "nowrap" },
  dateSep: { color: "#94a3b8", fontSize: "0.8rem" },
  dateClearBtn: {
    background: "none", border: "none", cursor: "pointer",
    color: "#94a3b8", padding: 0,
    display: "flex", alignItems: "center",
  },
  addBtn: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "7px 16px",
    background: "linear-gradient(135deg,#16632b,#1da144)",
    color: "#fff", border: "none", borderRadius: 9,
    fontSize: "0.82rem", fontWeight: 700, cursor: "pointer",
    fontFamily: "inherit", whiteSpace: "nowrap",
  },

  /* table */
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" },
  th: {
    padding: "10px 14px",
    background: "#f8fafc",
    color: "#64748b",
    fontWeight: 700,
    fontSize: "0.71rem",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    borderBottom: "1.5px solid #f1f5f9",
    whiteSpace: "nowrap",
    textAlign: "left",
  },
  td: {
    padding: "9px 14px",
    borderBottom: "1px solid #f8fafc",
    color: "#334155",
    verticalAlign: "middle",
  },
  emptyCell: {
    padding: "48px 20px",
    textAlign: "center",
    color: "#94a3b8",
    fontSize: "0.85rem",
  },

  /* pagination */
  pagination: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "10px 16px", borderTop: "1px solid #f1f5f9",
    flexWrap: "wrap", gap: 8,
  },
  pgInfo: { fontSize: "0.78rem", color: "#64748b" },
  pgBtns: { display: "flex", gap: 4 },
  pgBtn: {
    minWidth: 30, height: 30, border: "1px solid #e2e8f0",
    background: "#fff", borderRadius: 7, cursor: "pointer",
    fontSize: "0.78rem", color: "#475569", fontFamily: "inherit",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "all 0.15s",
  },
  pgBtnActive: {
    background: "linear-gradient(135deg,#16632b,#1da144)",
    color: "#fff", border: "none", fontWeight: 700,
  },
};

/* ─── Table cell styles ─── */
const ts = {
  uid:    { fontSize: "0.76rem", color: "#94a3b8", fontFamily: "monospace" },
  name:   { fontWeight: 600, color: "#1e293b", fontSize: "0.83rem", lineHeight: 1.3 },
  email:  { fontSize: "0.72rem", color: "#94a3b8", lineHeight: 1.2 },
  mono:   { fontFamily: "monospace", fontSize: "0.81rem" },
  wallet: { fontWeight: 700, color: "#16632b", fontSize: "0.83rem" },
  dateMain: { fontSize: "0.78rem", color: "#334155", fontWeight: 600, whiteSpace: "nowrap" },
  dateSub:  { fontSize: "0.68rem", color: "#94a3b8", whiteSpace: "nowrap" },
  badge: {
    display: "inline-flex", alignItems: "center",
    padding: "3px 9px", borderRadius: 100,
    fontSize: "0.7rem", fontWeight: 700,
    whiteSpace: "nowrap",
  },
  actionGroup: {
    display: "inline-flex", alignItems: "center", gap: 4,
    flexWrap: "nowrap",
  },
  actionDivider: {
    width: 1, height: 18, background: "#e2e8f0", margin: "0 2px",
  },
  iconBtn: {
    width: 28, height: 28,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    border: "1px solid transparent",
    borderRadius: 7, cursor: "pointer", fontFamily: "inherit",
    transition: "all 0.15s ease",
    padding: 0,
  },
  iconEdit:     { background: "#eff6ff", color: "#2563eb", borderColor: "#dbeafe" },
  iconWallet:   { background: "#f0fdf4", color: "#16a34a", borderColor: "#dcfce7" },
  iconPassword: { background: "#fdf4ff", color: "#9333ea", borderColor: "#f3e8ff" },
  iconPayment:  { background: "#f0f9ff", color: "#0369a1", borderColor: "#e0f2fe" },
  iconDelete:   { background: "#fef2f2", color: "#dc2626", borderColor: "#fee2e2" },
};
